import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { auth } from '../config/firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { saveWorkout } from '../utils/saveWorkout';
import ExerciseSlot from '../components/customWorkout/ExerciseSlot';
import ProgressionSuggestions from '../components/customWorkout/ProgressionSuggestions';
import PlaceholderImage from '../components/PlaceholderImage';
import { getExerciseProgressions } from '../utils/progressions';
import { generateSmartWorkout } from '../utils/workoutGeneration';
import exercisesData from '../data/exercises.json';

const CustomWorkoutBuilder = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [workout, setWorkout] = useState({
    name: '',
    description: '',
    exercises: [null, null, null, null, null] // 5 empty slots
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [showSearch, setShowSearch] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isProcessingPendingWorkout, setIsProcessingPendingWorkout] = useState(false);

  // Listen for auth state changes
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setUser(user);
      
      // Check for pending custom workout after login
      if (user) {
        const pendingAction = localStorage.getItem('pendingBookmarkAction');
        if (pendingAction) {
          try {
            const { action } = JSON.parse(pendingAction);
            if (action === 'saveCustomWorkout') {
              const pendingWorkout = localStorage.getItem('pendingCustomWorkout');
              if (pendingWorkout) {
                setIsProcessingPendingWorkout(true);
                try {
                  const workoutData = JSON.parse(pendingWorkout);
                  console.log('CustomWorkoutBuilder: Processing pending workout after login');
                  console.log('CustomWorkoutBuilder: workoutData:', workoutData);
                  console.log('CustomWorkoutBuilder: user:', user);
                  
                  // Automatically save the workout and redirect to library
                  try {
                    await saveWorkout({
                      user,
                      name: workoutData.name,
                      exercises: workoutData.exercises
                    });
                    console.log('CustomWorkoutBuilder: Workout saved successfully');
                    
                    toast.success('Custom workout saved successfully!');
                    // Set flag to show workouts tab and highlight new workout
                    localStorage.setItem('showWorkoutsTab', 'true');
                    localStorage.setItem('highlightNewWorkout', workoutData.name);
                    
                    // Clear the pending data
                    localStorage.removeItem('pendingCustomWorkout');
                    localStorage.removeItem('pendingBookmarkAction');
                    
                    // Navigate to library
                    navigate('/library');
                  } catch (error) {
                    console.error('CustomWorkoutBuilder: Error saving workout:', error);
                    toast.error('Failed to save workout. Please try again.');
                    setIsProcessingPendingWorkout(false);
                  }
                } catch (error) {
                  console.error('CustomWorkoutBuilder: Error parsing pending workout:', error);
                  setIsProcessingPendingWorkout(false);
                }
              }
            }
          } catch (error) {
            console.error('CustomWorkoutBuilder: Error parsing pending action:', error);
            setIsProcessingPendingWorkout(false);
          }
        }
      }
    });
    return () => unsubscribe();
  }, []);

  // Handle escape key to close progression suggestions
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && showSuggestions) {
        setShowSuggestions(false);
      }
    };
    
    if (showSuggestions) {
      document.addEventListener('keydown', handleEscape);
      return () => document.removeEventListener('keydown', handleEscape);
    }
  }, [showSuggestions]);

  // Search functionality
  useEffect(() => {
    if (searchQuery.trim()) {
      const filtered = exercisesData.filter(exercise =>
        exercise.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        exercise.primaryMuscles?.some(muscle => 
          muscle.toLowerCase().includes(searchQuery.toLowerCase())
        ) ||
        exercise.category?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      setSearchResults(filtered.slice(0, 10)); // Limit to 10 results
    } else {
      setSearchResults([]);
    }
  }, [searchQuery]);

  const handleExerciseSelect = (exercise, slotIndex) => {
    const newWorkout = { ...workout };
    newWorkout.exercises[slotIndex] = exercise;
    setWorkout(newWorkout);
    setShowSearch(false);
    setSearchQuery('');
    setSelectedSlot(null);
  };

  const handleRemoveExercise = (slotIndex) => {
    const newWorkout = { ...workout };
    newWorkout.exercises[slotIndex] = null;
    setWorkout(newWorkout);
  };

  const handleAddSlot = () => {
    const newWorkout = { ...workout };
    newWorkout.exercises.push(null);
    setWorkout(newWorkout);
  };

  const handleRemoveSlot = (slotIndex) => {
    if (workout.exercises.length > 1) {
      const newWorkout = { ...workout };
      newWorkout.exercises.splice(slotIndex, 1);
      setWorkout(newWorkout);
    }
  };

  const handleSlotClick = (slotIndex) => {
    setSelectedSlot(slotIndex);
    setShowSearch(true);
    setShowSuggestions(false);
  };

  const handleShowSuggestions = (exercise, slotIndex) => {
    if (exercise) {
      const progressions = getExerciseProgressions(exercise.id);
      setSuggestions({
        exercise,
        slotIndex,
        progressions
      });
      setShowSuggestions(true);
    }
  };

  const handleSuggestionSelect = (suggestedExercise) => {
    if (suggestions) {
      handleExerciseSelect(suggestedExercise, suggestions.slotIndex);
      setShowSuggestions(false);
      setSuggestions(null);
    }
  };

  const handleStartWorkout = () => {
    const filledSlots = workout.exercises.filter(ex => ex !== null);
    
    console.log('=== CustomWorkoutBuilder handleStartWorkout ===');
    console.log('Workout state:', workout);
    console.log('Filled slots:', filledSlots);
    console.log('Filled slots length:', filledSlots.length);
    
    if (filledSlots.length === 0) {
      toast.error('Please add at least one exercise to your workout');
      return;
    }

    if (!workout.name.trim()) {
      toast.error('Please give your workout a name');
      return;
    }

    const customWorkoutData = {
      name: workout.name,
      description: workout.description,
      exercises: filledSlots
    };
    
    console.log('Storing custom workout data:', customWorkoutData);
    
    // Store the workout data in localStorage for the workout flow
    localStorage.setItem('customWorkout', JSON.stringify(customWorkoutData));
    
    // Verify storage
    const stored = localStorage.getItem('customWorkout');
    console.log('Verified stored data:', stored);
    
    // Navigate to workout page using handleStart to process custom workout data
    // We need to trigger the handleStart function from App.js
    // For now, we'll use a different approach - store a flag to indicate this is a custom workout
    localStorage.setItem('isCustomWorkout', 'true');
    // Reset current step to 0 to ensure we start at step 1
    localStorage.setItem('currentStep', '0');
    navigate('/workout');
  };

  const handleSaveWorkout = async () => {
    const filledSlots = workout.exercises.filter(ex => ex !== null);
    
    if (filledSlots.length === 0) {
      toast.error('Please add at least one exercise to your workout');
      return;
    }

    if (!workout.name.trim()) {
      toast.error('Please give your workout a name');
      return;
    }

    // Check if user is logged in
    if (!user) {
      // Store the workout data for after login
      localStorage.setItem('pendingCustomWorkout', JSON.stringify({
        name: workout.name,
        description: workout.description,
        exercises: filledSlots
      }));
      localStorage.setItem('pendingBookmarkAction', JSON.stringify({
        action: 'saveCustomWorkout'
      }));
      toast.info('Please log in to save your custom workout');
      navigate('/login');
      return;
    }

    try {
      setIsSaving(true);
      // Save to Firestore using the same function as other workouts
      await saveWorkout({
        user,
        name: workout.name,
        exercises: filledSlots
      });
      
      toast.success('Custom workout saved successfully!');
      // Set flag to show workouts tab and highlight new workout
      localStorage.setItem('showWorkoutsTab', 'true');
      localStorage.setItem('highlightNewWorkout', workout.name);
      navigate('/library');
    } catch (error) {
      console.error('Error saving workout:', error);
      toast.error('Failed to save workout. Please try again.');
    } finally {
      setIsSaving(false);
    }
  };

  const filledSlots = workout.exercises.filter(ex => ex !== null).length;

  const handleQuickStart = (type) => {
    console.log('=== handleQuickStart called with type:', type);
    
    // Use smart workout generation for better exercise combinations
    const getRandomExercises = (criteria, count = 5) => {
      // Convert criteria to smart generation format
      const smartCriteria = {
        muscleGroups: criteria.primaryMuscles ? 
          criteria.primaryMuscles.map(muscle => {
            // Map specific muscles to muscle groups
            const muscleGroupMap = {
              'chest': 'chest',
              'shoulders': 'shoulders', 
              'biceps': 'biceps',
              'middle back': 'back',
              'quadriceps': 'legs',
              'hamstrings': 'legs',
              'glutes': 'glutes',
              'calves': 'legs',
              'abdominals': 'core'
            };
            return muscleGroupMap[muscle] || muscle;
          }) : [],
        category: criteria.category,
        level: criteria.level,
        equipment: criteria.equipment
      };
      
      return generateSmartWorkout(smartCriteria, count);
    };

    const quickStartData = {
      'upper': {
        criteria: { 
          primaryMuscles: ['chest', 'shoulders', 'biceps', 'middle back'],
          equipment: 'bodyweight'
        },
        title: 'Upper Body Strength',
        description: 'Build upper body strength with push and pull movements'
      },
      'lower': {
        criteria: { 
          primaryMuscles: ['quadriceps', 'hamstrings', 'glutes', 'calves'],
          equipment: 'bodyweight'
        },
        title: 'Lower Body Power',
        description: 'Strengthen your legs and glutes with bodyweight exercises'
      },
      'full': {
        criteria: { 
          primaryMuscles: ['chest', 'quadriceps', 'abdominals', 'shoulders'],
          equipment: 'bodyweight'
        },
        title: 'Full Body Blast',
        description: 'Complete workout targeting all major muscle groups'
      },
      'chest': {
        criteria: { 
          primaryMuscles: ['chest'],
          equipment: 'bodyweight'
        },
        title: 'Chest Focus Workout',
        description: 'Target your chest with various push-up variations'
      },
      'back': {
        criteria: { 
          primaryMuscles: ['middle back', 'shoulders', 'abdominals'],
          equipment: 'bodyweight'
        },
        title: 'Back & Core Strength',
        description: 'Strengthen your back and core with pulling movements'
      },
      'core': {
        criteria: { 
          primaryMuscles: ['abdominals'],
          equipment: 'bodyweight'
        },
        title: 'Core Conditioning',
        description: 'Build a strong core with stability and strength exercises'
      },
      'cardio': {
        criteria: { 
          category: 'cardio'
        },
        title: 'Cardio HIIT',
        description: 'High-intensity cardio workout to boost your heart rate'
      },
      'glutes': {
        criteria: { 
          primaryMuscles: ['glutes'],
          equipment: 'bodyweight'
        },
        title: 'Glutes & Booty',
        description: 'Target your glutes with focused strengthening exercises'
      }
    };

    const template = quickStartData[type];
    if (!template) return;

    // Get random exercises based on criteria
    const selectedExercises = getRandomExercises(template.criteria, 5);
    console.log(`Selected ${selectedExercises.length} exercises for ${type}:`, selectedExercises.map(ex => ex.name));

    const newWorkout = { 
      ...workout,
      name: template.title,
      description: template.description
    };
    
    // Clear existing exercises first
    newWorkout.exercises = [null, null, null, null, null];
    
    // Populate with selected exercises
    selectedExercises.forEach((exercise, index) => {
      if (index < newWorkout.exercises.length) {
        newWorkout.exercises[index] = exercise;
        console.log(`Added exercise to slot ${index}: ${exercise.name}`);
      }
    });

    // Ensure at least 3 exercises are added
    const filledExercises = newWorkout.exercises.filter(ex => ex !== null);
    console.log(`Filled ${filledExercises.length} exercises`);
    
    if (filledExercises.length < 3) {
      console.log('Not enough exercises found, adding fallback exercises...');
      // Get more exercises from the same criteria or broader criteria
      const fallbackCriteria = {
        ...template.criteria,
        equipment: 'bodyweight' // Ensure we get bodyweight exercises as fallback
      };
      const fallbackExercises = getRandomExercises(fallbackCriteria, 5);
      let fallbackIndex = 0;
      
      for (let i = 0; i < newWorkout.exercises.length && filledExercises.length < 3; i++) {
        if (newWorkout.exercises[i] === null && fallbackIndex < fallbackExercises.length) {
          newWorkout.exercises[i] = fallbackExercises[fallbackIndex];
          console.log(`Added fallback exercise: ${fallbackExercises[fallbackIndex].name}`);
          fallbackIndex++;
        }
      }
    }

    console.log('Setting new workout:', newWorkout);
    console.log('New workout exercises:', newWorkout.exercises);
    setWorkout(newWorkout);
    toast.success(`${template.title} started!`);
  };

  // Check if we're processing a pending workout (logged out user returning after login)
  const pendingAction = localStorage.getItem('pendingBookmarkAction');
  const isPendingWorkout = pendingAction && JSON.parse(pendingAction).action === 'saveCustomWorkout';

  // Show loading state while processing pending workout
  if (isProcessingPendingWorkout || (isPendingWorkout && !user)) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Saving your workout...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <style jsx>{`
        .scrollbar-hide {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        .scrollbar-hide::-webkit-scrollbar {
          display: none;
        }
      `}</style>
      <div className="max-w-4xl mx-auto px-4">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Create Custom Workout
          </h1>
          <p className="text-gray-600">
            Click on exercise slots below to search and add exercises. 
            Get progression suggestions to level up or add variety.
          </p>
        </div>

        {/* Quick Start Options */}
        {filledSlots === 0 && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Quick Start</h3>
              <span className="text-sm text-gray-500">Need inspiration? Choose a template:</span>
            </div>
            
            <div className="relative">
              {/* Scroll hint */}
              <div className="absolute right-0 top-0 z-10 bg-gradient-to-l from-white to-transparent w-8 h-full pointer-events-none"></div>
              <div className="absolute left-0 top-0 z-10 bg-gradient-to-r from-white to-transparent w-8 h-full pointer-events-none"></div>
              
              <div className="flex overflow-x-auto gap-4 pb-4 scrollbar-hide px-2">
                {[
                  { id: 'upper', name: 'Upper Body', icon: '💪', color: 'blue', description: 'Push-ups, Pull-ups, Dips, Plank, Pike Push-ups' },
                  { id: 'lower', name: 'Lower Body', icon: '🦵', color: 'green', description: 'Squats, Lunges, Calf Raises, Glute Bridges, Wall Sit' },
                  { id: 'full', name: 'Full Body', icon: '🔥', color: 'purple', description: 'Push-ups, Squats, Plank, Lunges, Mountain Climbers' },
                  { id: 'chest', name: 'Chest Focus', icon: '🏋️', color: 'red', description: 'Push-ups, Incline, Decline, Diamond, Wide-Grip' },
                  { id: 'back', name: 'Back Focus', icon: '🦾', color: 'indigo', description: 'Pull-ups, Inverted Row, Superman, Bird Dog, Dead Bug' },
                  { id: 'core', name: 'Core Focus', icon: '⚡', color: 'yellow', description: 'Plank, Mountain Climbers, Russian Twists, Bicycle Crunches' },
                  { id: 'cardio', name: 'Cardio HIIT', icon: '❤️', color: 'pink', description: 'Jumping Jacks, Mountain Climbers, Burpees, High Knees' },
                  { id: 'glutes', name: 'Glutes Focus', icon: '🍑', color: 'orange', description: 'Glute Bridges, Hip Thrusts, Fire Hydrants, Clamshells' }
                ].map((template) => (
                  <button
                    key={template.id}
                    onClick={() => handleQuickStart(template.id)}
                    className={`flex-shrink-0 w-64 p-4 border-2 border-gray-200 rounded-xl hover:border-${template.color}-300 hover:bg-${template.color}-50 transition-all duration-200 text-left group hover:shadow-md min-h-[140px] flex flex-col`}
                  >
                    <div className="flex items-start space-x-3 flex-1">
                      <div className="text-3xl group-hover:scale-110 transition-transform duration-200">
                        {template.icon}
                      </div>
                      <div className="flex-1 min-w-0 flex flex-col">
                        <h4 className="font-semibold text-gray-900 text-lg mb-1">
                          {template.name}
                        </h4>
                        <p className="text-sm text-gray-600 leading-relaxed flex-1">
                          {template.description}
                        </p>
                        <div className="mt-2 flex items-center text-xs text-gray-500">
                          <span className="inline-flex items-center px-2 py-1 rounded-full bg-gray-100">
                            <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                            </svg>
                            Quick Start
                          </span>
                        </div>
                      </div>
                    </div>
                  </button>
                ))}
              </div>
              
              {/* Scroll hint text */}
              <div className="flex justify-center mt-2">
                <span className="text-xs text-gray-400 flex items-center">
                  <svg className="w-3 h-3 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16l-4-4m0 0l4-4m-4 4h18" />
                  </svg>
                  Scroll for more options
                  <svg className="w-3 h-3 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
        )}


        {/* Exercise Slots */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">
              Exercise Slots ({filledSlots}/{workout.exercises.length})
            </h2>
            <div className="flex items-center gap-3">
              <button
                onClick={handleAddSlot}
                className="px-3 py-1 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors text-sm font-medium flex items-center gap-1"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                </svg>
                Add Slot
              </button>
              <div className="text-sm text-gray-500">
                Click on a slot to add an exercise
              </div>
            </div>
          </div>
          
          <div className="space-y-3">
            {workout.exercises.map((exercise, index) => (
              <div
                key={index}
                className={showSearch && selectedSlot === index ? 'ring-2 ring-blue-500 ring-opacity-50 rounded-lg' : ''}
              >
                <ExerciseSlot
                  exercise={exercise}
                  slotIndex={index}
                  onSlotClick={() => handleSlotClick(index)}
                  onRemoveExercise={() => handleRemoveExercise(index)}
                  onShowSuggestions={() => handleShowSuggestions(exercise, index)}
                  onRemoveSlot={() => handleRemoveSlot(index)}
                  canRemoveSlot={workout.exercises.length > 1}
                  isSelected={showSearch && selectedSlot === index}
                  isShowingProgressions={showSuggestions && suggestions && suggestions.slotIndex === index}
                />
              </div>
            ))}
          </div>
        </div>

        {/* Search Results */}
        {showSearch && (
          <div className="bg-blue-50 rounded-lg shadow-lg p-6 mb-6 border-2 border-blue-300">
            <div className="mb-4">
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Search & Add Exercise to Slot {selectedSlot + 1}
                  </h3>
                </div>
                <button
                  onClick={() => {
                    setShowSearch(false);
                    setSelectedSlot(null);
                    setSearchQuery('');
                  }}
                  className="text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Type to search for exercises..."
                  className="w-full px-4 py-3 pl-10 border-2 border-blue-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-lg"
                  autoFocus
                />
                <svg className="absolute left-3 top-3.5 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
            </div>
            
            {searchResults.length > 0 ? (
              <div className="space-y-2">
                {searchResults.map((exercise) => (
                  <div
                    key={exercise.id}
                    onClick={() => handleExerciseSelect(exercise, selectedSlot)}
                    className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 cursor-pointer transition-colors"
                  >
                    <div className="w-12 h-12 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                      {exercise.images && exercise.images.length > 0 ? (
                        exercise.images[0] === "placeholder.jpg" ? (
                          <PlaceholderImage 
                            className="w-full h-full"
                          />
                        ) : (
                          <img
                            src={`/exercise-images/${exercise.images[0]}`}
                            alt={exercise.name}
                            className="w-full h-full object-cover"
                          />
                        )
                      ) : (
                        <div className="w-full h-full bg-gray-200 flex items-center justify-center">
                          <span className="text-gray-400 text-xs">📷</span>
                        </div>
                      )}
                    </div>
                    <div className="ml-3 flex-1">
                      <h3 className="font-medium text-gray-900">{exercise.name}</h3>
                      <p className="text-sm text-gray-500">
                        {exercise.primaryMuscles && exercise.primaryMuscles.length > 0
                          ? exercise.primaryMuscles[0]
                          : 'Multiple muscles'
                        }
                      </p>
                    </div>
                    <div className="text-blue-600">
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                    </div>
                  </div>
                ))}
              </div>
            ) : searchQuery.trim() ? (
              <p className="text-gray-500 text-center py-4">No exercises found</p>
            ) : (
              <p className="text-gray-500 text-center py-4">Start typing to search for exercises</p>
            )}
          </div>
        )}

        {/* Progression Suggestions Modal */}
        {showSuggestions && suggestions && (
          <div 
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) {
                setShowSuggestions(false);
              }
            }}
          >
            <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
              <ProgressionSuggestions
                suggestions={suggestions}
                onSelect={handleSuggestionSelect}
                onClose={() => setShowSuggestions(false)}
              />
            </div>
          </div>
        )}

        {/* Workout Details */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">
            Workout Details
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Workout Name
              </label>
              <input
                type="text"
                value={workout.name}
                onChange={(e) => setWorkout({...workout, name: e.target.value})}
                placeholder="e.g., Upper Body Strength"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Description (Optional)
              </label>
              <input
                type="text"
                value={workout.description}
                onChange={(e) => setWorkout({...workout, description: e.target.value})}
                placeholder="e.g., Focus on chest and shoulders"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex justify-end items-center gap-4">
          {/* Save Button */}
          <button
            onClick={handleSaveWorkout}
            disabled={filledSlots === 0 || isSaving}
            className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium flex items-center gap-2"
          >
            {isSaving ? (
              <>
                <svg className="animate-spin h-4 w-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </>
            ) : (
              user ? 'Save Workout for Later' : 'Login to Save Workout for Later'
            )}
          </button>

          {/* Start Workout Button */}
          <button
            onClick={handleStartWorkout}
            disabled={filledSlots === 0}
            className="px-6 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed font-medium flex items-center gap-2"
          >
            Confirm and Start Workout Now
          </button>
        </div>
      </div>
    </div>
  );
};

export default CustomWorkoutBuilder;
